<div class="page-inner-body portlet light">
  <div class="portlet-body">
    <!-- Top Action Bar -->
    <div class="top-action-bar">
      <div class="page-title">{{ 'labelsGeneration.generateDocuments' | transloco }}</div>
      <p class="page-subtitle mt-2 mb-0">
        {{ 'labelsGeneration.pageSubtitle' | transloco }}
      </p>
    </div>

    <!-- Skeleton Loader -->
    @if (isLoading) {
      <div class="row my-2">
        @for (placeholder of [1,2,3,4,5,6]; track placeholder) {
          <div class="col-4 mt-3">
            <div class="skeleton-card">
              <div class="skeleton-image"></div>
              <div class="skeleton-details">
                <div class="skeleton-text skeleton-title"></div>
                <div class="skeleton-text skeleton-line"></div>
                <div class="skeleton-text skeleton-line short"></div>
                <div class="skeleton-text skeleton-line"></div>
              </div>
            </div>
          </div>
        }
        <div class="col-12 text-center text-muted small mt-3">
          <i class="pi pi-spin pi-spinner me-2"></i> {{ 'labelsGeneration.loadingLabels' | transloco }}
        </div>
      </div>
    }

    <!-- Main content with available and selected products -->
    <div class="row p-2">
      <!-- Available Products Section -->
      <div class="col-9 border px-3 py-3 bg-white shadow-sm">
        <div class="row">
          <!-- Section header -->
          <div class="d-flex justify-content-between align-items-center mb-2 px-2">
            <div class="fw-bold fs-5">
              <i class="pi pi-images color-artwork me-1"></i> {{ 'labelsGeneration.products' | transloco }}
            </div>
          </div>
          <!-- Helper text -->
          <div class="text-muted small px-2 mb-3">
            {{ 'labelsGeneration.availableHelper' | transloco }}
          </div>

          <!-- Product cards -->
          @for (product of galleryProducts; track product) {
            <div class="col-2 p-1 mb-2">
              <div class="d-flex flex-column align-items-center rounded-3 shadow bg-light border h-100 cursor-pointer"
                (click)="selectProduct(product); $event.stopPropagation()"
                [title]="'labelsGeneration.selectForGeneration' | transloco: { name: getProductName(product) }">
                <div
                  class="image-container d-flex justify-content-center align-items-center p-1 border-bottom w-100 position-relative"
                  [title]="'labelsGeneration.viewDetailsOf' | transloco: { name: getProductName(product) }">
                  <!-- View icon for product details -->
                  <div class="position-absolute top-0 end-0 m-2"
                    (click)="openProductDialog(getProductId(product)); $event.stopPropagation()">
                    <i class="fa fa-info-circle fs-4 text-black"></i>
                  </div>
                  <img [src]="getProductImageUrl(product)" class="img-fluid rounded w-100" style="object-fit: contain;" />
                </div>
                <div class="text-center px-2 py-2 w-100">
                  <div class="text-uppercase fw-semibold text-dark text-truncate small">
                    {{ getProductName(product) }}
                  </div>
                </div>
              </div>
            </div>
          }

          <!-- No products message -->
          @if (galleryProducts.length === 0) {
            <div class="d-flex justify-content-center text-muted py-5">
              <i class="pi pi-image me-2"></i> {{ 'labelsGeneration.noProductsAvailable' | transloco }}
            </div>
          }
        </div>
      </div>

      <!-- Selected Products Section -->
      <div class="col-3 border py-3 bg-white shadow-sm rounded">
        <div class="row p-2">
          <!-- Section header -->
          <div class="d-flex flex-column mb-2 px-2">
            <div class="fw-bold fs-4 text-center mb-3" style="color: #178d9b;">
              <i class="pi pi-tags me-1"></i> {{ 'labelsGeneration.selectedProducts' | transloco }}
            </div>

            <!-- Selected products count badge -->
            <div class="d-flex justify-content-center mb-3">
              @if (selectedProducts.length > 0) {
                <span
                  class="badge text-white d-inline-flex align-items-center justify-content-center"
                  style="background-color: #178d9b; font-weight: 700; width: 40px; height: 40px; border-radius: 50%; font-size: 1.1rem;">
                  {{ selectedProducts.length }}
                </span>
              }
            </div>

            <!-- Action Buttons Card -->
            @if (selectedProducts.length > 0) {
              <div class="card shadow-sm border-0 mb-3">
                <div class="card-body p-3">
                  <h6 class="card-title text-center mb-3 fw-bold" style="color: #333;">{{ 'labelsGeneration.generateDocuments' | transloco }}</h6>
                  <div class="d-flex flex-column gap-3">
                    <!-- Generate Info Cards button -->
                    <button pButton type="button" [label]="'labelsGeneration.infoCards' | transloco" icon="pi pi-file-pdf"
                      class="p-button-lg p-button-outlined w-100"
                      style="background-color: #f8f9fa; border-left: 4px solid #178d9b; color: #178d9b;"
                      (click)="onGenerateInfoCards()">
                    </button>
                    <!-- Barcodes Stickers button -->
                    <button pButton type="button" [label]="'labelsGeneration.barcodesStickers' | transloco" class="p-button-lg p-button-outlined w-100"
                      style="background-color: #f8f9fa; border-left: 4px solid #F97316; color: #F97316;"
                      (click)="onGenerateStickers()">
                      <i class="fa fa-barcode mr-2"></i>
                    </button>
                  </div>
                  <!-- Helper text -->
                  <div class="mt-3 mb-0 py-2 px-3" role="alert" style="font-size: 0.85rem;">
                    <i class="pi pi-info-circle me-1"></i>
                    {{ 'labelsGeneration.clickToDownload' | transloco }}
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- No products selected message -->
          @if (selectedProducts.length === 0) {
            <div class="d-flex justify-content-center text-muted text-center-4">
              <i class="pi pi-info-circle me-1 mt-1"></i>
              {{ 'labelsGeneration.noProductsSelected' | transloco }}
            </div>
          }

          <!-- Selected product cards -->
          @for (product of selectedProducts; track product; let i = $index) {
            <div class="col-6 mb-3 p-1">
              <div class="d-flex flex-column align-items-center rounded-3 shadow bg-light border h-100 cursor-pointer"
                (click)="removeFromSelection(product); $event.stopPropagation()"
                [title]="'labelsGeneration.removeFromSelection' | transloco: { name: getProductName(product) }">
                <div
                  class="image-container d-flex justify-content-center align-items-center p-1 border-bottom w-100 position-relative"
                  [title]="'labelsGeneration.viewDetailsOf' | transloco: { name: getProductName(product) }">
                  <!-- View icon for product details -->
                  <div class="position-absolute top-0 end-0 m-2"
                    (click)="openProductDialog(getProductId(product)); $event.stopPropagation()">
                    <i class="fa fa-info-circle fs-4 text-black"></i>
                  </div>
                  <img [src]="getProductImageUrl(product)" class="img-fluid rounded w-100" style="object-fit: contain;" />
                </div>
                <div class="text-center px-2 py-2 w-100">
                  <div class="text-uppercase fw-semibold text-dark text-truncate small">
                    {{ getProductName(product) }}
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Product details dialog -->
@if (showProductDialog) {
  <app-product-details-dialog [showProductDialog]="showProductDialog" [productId]="productId"
  (onClose)="closeProductDialog()"></app-product-details-dialog>
}